import { ChevronLeft } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-rdc-cream">
      {/* Header */}
      <div className="bg-white border-b border-rdc-stone/30">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-rdc-taupe hover:text-rdc-charcoal transition-colors mb-6"
          >
            <ChevronLeft size={18} />
            <span className="text-sm font-medium">Back</span>
          </Link>
          <h1 className="page-title">Privacy Policy</h1>
          <p className="text-rdc-taupe mt-2">
            Last updated: February 16, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-sm max-w-none text-rdc-charcoal">
          {/* Introduction */}
          <section className="mb-8">
            <p className="text-base leading-relaxed mb-4">
              Riding & Driving Club ("RDC", "we", "us", or "our") operates the Digital Garage platform at ridinganddrivingclub.com. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application.
            </p>
            <p className="text-base leading-relaxed">
              Please read this Privacy Policy carefully. If you do not agree with our practices, please do not use our application. By accessing and using RDC Digital Garage, you acknowledge that you have read, understood, and agree to be bound by all the terms of this Privacy Policy.
            </p>
          </section>

          {/* 1. Information We Collect */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">1. Information We Collect</h2>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Account Information</h3>
              <p className="text-base leading-relaxed mb-3">
                When you create an account, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal">
                <li>Full name and email address</li>
                <li>Phone number (including for SMS notifications)</li>
                <li>Password and account credentials</li>
                <li>Company or organization name (optional)</li>
                <li>Account creation date and member status</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Vehicle Information</h3>
              <p className="text-base leading-relaxed mb-3">
                To track and manage your vehicle collection, we collect and store:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal">
                <li>Vehicle make, model, year, and VIN</li>
                <li>Vehicle photos and specifications</li>
                <li>Service and maintenance records</li>
                <li>Valuation history and appraisals</li>
                <li>Registration and insurance information</li>
                <li>Vehicle location and storage details</li>
                <li>Ownership and title information</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Service and Usage Data</h3>
              <p className="text-base leading-relaxed mb-3">
                We collect information about how you interact with our platform:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal">
                <li>Appointments and work orders</li>
                <li>Documents uploaded and stored</li>
                <li>Service provider interactions</li>
                <li>Community participation and discussions</li>
                <li>Usage patterns and feature interactions</li>
                <li>Browser and device information</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Communications Data</h3>
              <p className="text-base leading-relaxed">
                When we communicate with you via SMS, email, or phone, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal">
                <li>Messages sent and received</li>
                <li>Communication preferences</li>
                <li>Timestamps of all communications</li>
              </ul>
            </div>
          </section>

          {/* 2. How We Use Your Information */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">2. How We Use Your Information</h2>
            <p className="text-base leading-relaxed mb-4">We use the information we collect for:</p>

            <ul className="list-disc list-inside space-y-3 text-base text-rdc-charcoal">
              <li><strong>Account Management:</strong> Creating and maintaining your account, authentication, and profile management</li>
              <li><strong>Service Delivery:</strong> Providing the Digital Garage platform and all features, including vehicle tracking and maintenance alerts</li>
              <li><strong>AI Concierge Service:</strong> Powering our AI-assisted concierge service to help with appointments, service coordination, and vehicle inquiries</li>
              <li><strong>SMS/Text Notifications:</strong> Sending work order updates, appointment reminders, service alerts, and concierge notifications to your provided phone number</li>
              <li><strong>Email Communications:</strong> Sending reminders, notifications, and important account information</li>
              <li><strong>Service Coordination:</strong> Connecting you with service providers and specialists in our directory</li>
              <li><strong>Customer Support:</strong> Responding to inquiries and providing technical support</li>
              <li><strong>Platform Improvement:</strong> Analyzing usage patterns to improve features and user experience</li>
              <li><strong>Compliance:</strong> Meeting legal, regulatory, and contractual obligations</li>
              <li><strong>Community Features:</strong> Enabling club directory, event coordination, and community discussions</li>
            </ul>
          </section>

          {/* 3. SMS/Text Messaging Terms */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">3. SMS/Text Messaging Terms</h2>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Types of Messages</h3>
              <p className="text-base leading-relaxed mb-3">
                By providing your phone number, you consent to receive SMS text messages from Riding & Driving Club, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal">
                <li><strong>Transactional Messages:</strong> Work order status updates, appointment confirmations, and service notifications</li>
                <li><strong>Informational Messages:</strong> Service reminders, maintenance alerts, and vehicle-related notifications</li>
                <li><strong>Concierge Notifications:</strong> Updates from our AI concierge service regarding your requests and interactions</li>
                <li><strong>Account Messages:</strong> Password resets, security alerts, and account-related updates</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Message Frequency</h3>
              <p className="text-base leading-relaxed">
                Message frequency varies based on your account activity and preferences. Typically, you can expect 1-5 messages per month, but this may vary depending on service requests, appointments, and alerts. Standard message and data rates may apply according to your mobile service provider's plan.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">How to Opt Out</h3>
              <p className="text-base leading-relaxed">
                You can opt out of SMS messages at any time by replying "STOP" to any message. After opting out, we will no longer send SMS messages to your phone, except as required by law. Please note that opting out of SMS notifications may affect the functionality of certain features that rely on text message delivery.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Get Help</h3>
              <p className="text-base leading-relaxed">
                If you have questions about SMS messages or need help, reply "HELP" to any message or contact our support team at michael@ridinganddrivingclub.com. We're happy to assist with any questions about our messaging service.
              </p>
            </div>
          </section>

          {/* 4. Third-Party Services */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">4. Third-Party Services and Data Sharing</h2>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Service Providers</h3>
              <p className="text-base leading-relaxed mb-3">
                We share your information with the following third-party service providers to deliver our services:
              </p>
              <ul className="list-disc list-inside space-y-3 text-base text-rdc-charcoal">
                <li><strong>Supabase:</strong> Our database and backend infrastructure provider. They store and manage your account data, vehicle information, and service records.</li>
                <li><strong>Twilio:</strong> Our SMS and voice communications provider. Twilio handles the delivery of text messages and phone communications on our behalf.</li>
                <li><strong>Anthropic (Claude):</strong> Our AI service provider. Anthropic provides the AI technology that powers our concierge service and assists with vehicle-related inquiries.</li>
                <li><strong>Resend:</strong> Our email delivery service provider. Resend handles sending transactional and informational emails on our behalf.</li>
              </ul>
              <p className="text-base leading-relaxed mt-4">
                These service providers are contractually obligated to use your information only as necessary to provide services to us and are bound by confidentiality agreements.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Service Provider Directory</h3>
              <p className="text-base leading-relaxed">
                When you contact service providers in our directory (mechanics, insurance agents, specialists), we may share relevant vehicle information and your contact details to facilitate service coordination. You maintain control over what information is shared with specific providers.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Legal Requirements</h3>
              <p className="text-base leading-relaxed">
                We may disclose information when required by law, regulation, or legal process, or when we believe in good faith that disclosure is necessary to protect our rights, your safety, or the rights and safety of others.
              </p>
            </div>
          </section>

          {/* 5. Data Security */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">5. Data Security</h2>
            <p className="text-base leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal">
              <li>Encryption of sensitive data in transit and at rest</li>
              <li>Secure password storage using industry-standard hashing</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Limited access controls to personal information</li>
              <li>Secure socket layer (SSL) technology for data transmission</li>
              <li>Compliance with industry security standards</li>
            </ul>
            <p className="text-base leading-relaxed mt-4">
              While we strive to protect your information, no method of transmission over the internet or electronic storage is completely secure. We cannot guarantee absolute security of any information you transmit to us.
            </p>
          </section>

          {/* 6. Data Retention */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">6. Data Retention</h2>
            <p className="text-base leading-relaxed mb-4">
              We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy. Specifically:
            </p>
            <ul className="list-disc list-inside space-y-3 text-base text-rdc-charcoal">
              <li><strong>Account Information:</strong> Retained while your account is active and for 12 months after account deletion (for backup and legal purposes)</li>
              <li><strong>Vehicle Information:</strong> Retained while you maintain the vehicle in your garage and for 36 months after deletion (for historical records)</li>
              <li><strong>Service Records:</strong> Retained indefinitely as part of your vehicle's history and for valuation purposes</li>
              <li><strong>Communication Records:</strong> Retained for 12 months for support and dispute resolution purposes</li>
              <li><strong>SMS Messages:</strong> Retained for 90 days for compliance and dispute resolution</li>
            </ul>
            <p className="text-base leading-relaxed mt-4">
              You may request deletion of your personal information at any time, subject to legal and contractual retention requirements.
            </p>
          </section>

          {/* 7. Your Rights and Choices */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">7. Your Rights and Choices</h2>
            <p className="text-base leading-relaxed mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside space-y-3 text-base text-rdc-charcoal">
              <li><strong>Access:</strong> You have the right to access and review your personal information at any time through your account settings</li>
              <li><strong>Correction:</strong> You can update or correct inaccurate information through your account profile</li>
              <li><strong>Deletion:</strong> You can request deletion of your account and associated personal information, subject to legal retention requirements</li>
              <li><strong>Opt-out:</strong> You can opt out of specific communications (SMS, email reminders) through your notification preferences or by contacting us</li>
              <li><strong>Data Portability:</strong> You can request a copy of your data in a portable format</li>
              <li><strong>Notification Preferences:</strong> You can control what types of messages and notifications you receive through your settings</li>
            </ul>
            <p className="text-base leading-relaxed mt-4">
              To exercise any of these rights, please contact us at michael@ridinganddrivingclub.com.
            </p>
          </section>

          {/* 8. Cookies and Analytics */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">8. Cookies and Analytics</h2>
            <p className="text-base leading-relaxed mb-4">
              We use cookies and similar technologies to enhance your experience and analyze platform usage:
            </p>
            <div className="mb-4">
              <h3 className="font-semibold text-rdc-black mb-3">Types of Cookies</h3>
              <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal">
                <li><strong>Essential Cookies:</strong> Required for authentication and platform functionality</li>
                <li><strong>Performance Cookies:</strong> Help us understand how you use our platform</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
            </div>
            <p className="text-base leading-relaxed">
              You can control cookie preferences through your browser settings. Disabling certain cookies may affect platform functionality.
            </p>
          </section>

          {/* 9. Children's Privacy */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">9. Children's Privacy</h2>
            <p className="text-base leading-relaxed">
              The Digital Garage platform is not intended for children under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that a child under 18 has provided us with personal information, we will delete such information and terminate the child's account. If you believe we have collected information from a child under 18, please contact us immediately at michael@ridinganddrivingclub.com.
            </p>
          </section>

          {/* 10. Changes to This Privacy Policy */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">10. Changes to This Privacy Policy</h2>
            <p className="text-base leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated policy on our website and updating the "Last updated" date. Your continued use of our application following the posting of revised Privacy Policy means that you accept and agree to the changes. We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
            </p>
          </section>

          {/* 11. Contact Information */}
          <section className="mb-12">
            <h2 className="section-title mb-4 text-rdc-black">11. Contact Information</h2>
            <p className="text-base leading-relaxed mb-4">
              If you have questions about this Privacy Policy, our privacy practices, or wish to exercise your rights, please contact us:
            </p>
            <div className="bg-rdc-cream rounded-lg p-6 border border-rdc-stone/30">
              <p className="text-base"><strong>Riding & Driving Club</strong></p>
              <p className="text-base text-rdc-charcoal mt-2">
                Email: michael@ridinganddrivingclub.com
              </p>
              <p className="text-base text-rdc-charcoal mt-2">
                Website: ridinganddrivingclub.com
              </p>
              <p className="text-sm text-rdc-taupe mt-4">
                We will respond to all privacy inquiries within 30 days of receipt.
              </p>
            </div>
          </section>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-rdc-stone/30 flex gap-6 justify-center text-sm">
            <Link to="/terms" className="text-rdc-primary hover:underline">
              Terms of Service
            </Link>
            <span className="text-rdc-stone">/</span>
            <Link to="/sms-consent" className="text-rdc-primary hover:underline">
              SMS Consent
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
